import * as THREE from "three";
import type { Biome } from "../ui/chapters";

/**
 * Stand-in albedos for a biome whose own terrain map set has not shipped.
 *
 * The client loads `hq/terrain/<biome>/` first (GroundMesh.load). The marsh and ashlands sets were authored in an art
 * session but never reached the repo, so levels 21–60 found no maps, `GroundMesh.available` went false and those
 * levels fell back to the old tile kit: flat teal tiles and the low-poly surround. Rather than fall that far, a
 * missing biome now borrows the meadow's maps — normals, roughness and height are material-neutral — and recolours
 * only the albedos here, once per biome at load. A real set dropped into `hq/terrain/<biome>/` wins automatically.
 *
 * The recolour is a gradient map: each pixel's luminance, stretched between the image's own 2nd and 98th percentiles,
 * picks a colour on a three-stop ramp (shadow, mid, light). The meadow's baked relief and cavity shadow live in that
 * luminance, so the detail survives while the palette becomes bog, peat and boardwalk, or ash, cinder and basalt.
 */

type Rgb = readonly [number, number, number];
/** Shadow, mid and light colours in sRGB 0–255. */
export type Ramp = readonly [Rgb, Rgb, Rgb];
export type Slot = "grass" | "dirt" | "cobble" | "rock" | "soil";

/** Per-biome ramps for the five terrain slots (the slots are listed in GroundMesh.load). */
export const BiomeRamps: Partial<Record<Biome, Record<Slot, Ramp>>> = {
  marsh: {
    grass: [[26, 32, 18], [74, 84, 44], [138, 136, 80]],      // bog moss: olive, never the meadow's green
    dirt: [[30, 23, 15], [78, 62, 42], [124, 106, 76]],       // mud
    cobble: [[34, 28, 22], [88, 72, 54], [146, 124, 94]],     // boardwalk and wet stone
    rock: [[30, 33, 28], [82, 86, 72], [142, 142, 122]],      // marsh rock, lichen-grey
    soil: [[22, 17, 12], [56, 44, 30], [98, 78, 54]],         // peat
  },
  ashlands: {
    grass: [[38, 31, 27], [100, 87, 76], [162, 144, 124]],    // ash — warm, or the scene's blue sky light turns it slate
    dirt: [[26, 18, 13], [70, 48, 34], [120, 88, 62]],        // scorch
    cobble: [[16, 16, 18], [46, 43, 45], [90, 84, 82]],       // basalt road
    rock: [[22, 16, 15], [60, 46, 40], [108, 84, 70]],        // lava rock
    soil: [[32, 13, 8], [82, 38, 18], [136, 70, 32]],         // cinder
  },
};

/** Luminance percentiles that map to the ends of the ramp; the tails clip so one stray pixel cannot flatten the rest. */
const LowPercentile = 0.02;
const HighPercentile = 0.98;
/** How much of the source colour survives, so the meadow's warm/cool drift still breaks up the ramp a little. */
const KeepSource = 0.08;

const luma = (r: number, g: number, b: number): number => (r * 54 + g * 183 + b * 19) >> 8;   // Rec. 709 weights / 256

/** Recolours RGBA pixels in place along `ramp`. Pure, so it is unit-tested without a canvas. */
export function gradeRgba(px: Uint8ClampedArray, ramp: Ramp): void {
  const hist = new Uint32Array(256);
  const n = px.length >> 2;
  for (let i = 0; i < px.length; i += 4) hist[luma(px[i], px[i + 1], px[i + 2])]++;
  const lo = percentile(hist, n, LowPercentile), hi = Math.max(lo + 1, percentile(hist, n, HighPercentile));
  const lut = new Uint8ClampedArray(256 * 3);                 // luminance → ramp colour, so the pixel loop is lookups
  for (let y = 0; y < 256; y++) {
    const t = Math.min(1, Math.max(0, (y - lo) / (hi - lo)));
    const [a, b, u] = t < 0.5 ? [ramp[0], ramp[1], t * 2] : [ramp[1], ramp[2], t * 2 - 1];
    for (let c = 0; c < 3; c++) lut[y * 3 + c] = a[c] + (b[c] - a[c]) * u;
  }
  for (let i = 0; i < px.length; i += 4) {
    const y = luma(px[i], px[i + 1], px[i + 2]) * 3;
    for (let c = 0; c < 3; c++) px[i + c] = lut[y + c] + (px[i + c] - lut[y + c]) * KeepSource;
  }
}

function percentile(hist: Uint32Array, n: number, p: number): number {
  const target = n * p;
  let seen = 0;
  for (let y = 0; y < 256; y++) { seen += hist[y]; if (seen >= target) return y; }
  return 255;
}

/** A graded copy of a loaded albedo, as a texture with the same wrapping, colour space and filtering. `src` is untouched. */
export function gradedTexture(src: THREE.Texture, ramp: Ramp): THREE.Texture {
  const img = src.image as CanvasImageSource & { width: number; height: number };
  const canvas = document.createElement("canvas");
  canvas.width = img.width; canvas.height = img.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return src;                                       // no 2D context: an ungraded ground beats the tile kit
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  gradeRgba(data.data, ramp);
  ctx.putImageData(data, 0, 0);
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = src.wrapS; t.wrapT = src.wrapT; t.colorSpace = src.colorSpace; t.anisotropy = src.anisotropy;
  return t;                                                   // `src` stays: it is the meadow's own albedo
}
